// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUser, clearSession } from '../utils/auth.js';
import { api } from '../utils/api.js';
import { PlanCards } from '../components/shared/PaywallModal.jsx';
import Logo from '../components/shared/Logo.jsx';

const PLAN_LIMITS = {
  free:             { catalogs: 0,  label: 'Free' },
  starter:          { catalogs: 5,  label: 'Starter' },
  'starter-annual': { catalogs: 5,  label: 'Starter' },
  growth:           { catalogs: 15, label: 'Growth' },
  'growth-annual':  { catalogs: 15, label: 'Growth' },
  pro:              { catalogs: 50, label: 'Pro' },
  'pro-annual':     { catalogs: 50, label: 'Pro' },
};

const PLAN_COLORS = {
  free:    { bg: '#f3f4f6', text: '#6b7280', border: '#e5e7eb' },
  starter: { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
  growth:  { bg: '#f5f3ff', text: '#6d28d9', border: '#ddd6fe' },
  pro:     { bg: '#fef3c7', text: 'var(--color-yellow-text)', border: 'var(--color-yellow-border)' },
};

function useDarkMode() {
  const [dark, setDark] = useState(() => {
    try { return localStorage.getItem('cc_theme') === 'dark'; } catch { return false; }
  });
  function toggle() {
    setDark(d => {
      const next = !d;
      document.documentElement.setAttribute('data-theme', next ? 'dark' : '');
      try { localStorage.setItem('cc_theme', next ? 'dark' : 'light'); } catch {}
      return next;
    });
  }
  return [dark, toggle];
}

export default function Settings() {
  const navigate  = useNavigate();
  const user      = getUser();
  const [dark, toggleDark] = useDarkMode();
  const [deleting,     setDeleting]     = useState(false);
  const [showDelConf,  setShowDelConf]  = useState(false);
  const [resetSent,    setResetSent]    = useState(false);
  const [resetErr,     setResetErr]     = useState('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalErr,     setPortalErr]     = useState('');
  const [me,           setMe]           = useState(null);
  const [upgradingPlan, setUpgradingPlan] = useState(null);

  useEffect(() => {
    api.getMe().then(setMe).catch(() => {});
  }, []);

  const plan      = me?.plan ?? user?.plan ?? 'free';
  const isPaid    = plan !== 'free';
  const planMeta  = PLAN_LIMITS[plan] ?? PLAN_LIMITS.free;
  const planColor = PLAN_COLORS[plan] ?? PLAN_COLORS.free;
  const used      = me?.catalogs_processed_this_month ?? 0;
  const quota     = planMeta.catalogs;
  const usePct    = quota > 0 ? Math.min(100, Math.round((used / quota) * 100)) : 0;

  async function handleLogout() {
    try { await api.logout(); } catch {}
    clearSession();
    navigate('/login');
  }

  async function handlePasswordReset() {
    setResetErr('');
    try {
      await api.resetPassword(user?.email);
      setResetSent(true);
    } catch (e) {
      setResetErr(e.message || 'Failed to send reset email');
    }
  }

  async function handleBillingPortal() {
    setPortalErr('');
    setPortalLoading(true);
    try {
      const { url } = await api.createPortalSession();
      window.location.href = url;
    } catch (e) {
      setPortalErr(e.message || 'Could not open billing portal.');
      setPortalLoading(false);
    }
  }

  async function handleUpgrade(planKey) {
    setUpgradingPlan(planKey);
    try {
      const { url } = await api.createCheckoutSession(planKey, '/settings');
      window.location.href = url;
    } catch (e) {
      alert(e.message || 'Could not start checkout. Please try again.');
      setUpgradingPlan(null);
    }
  }

  async function handleDeleteAccount() {
    setDeleting(true);
    try {
      await api.deleteAccount();
      clearSession();
      navigate('/login');
    } catch (e) {
      alert(e.message || 'Failed to delete account');
      setDeleting(false);
      setShowDelConf(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
      <header style={{
        position: 'sticky', top: 0, zIndex: 20,
        background: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)',
      }}>
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Logo iconSize={24} onClick={() => navigate('/dashboard')} />
            <span style={{ color: 'var(--color-text-3)' }}>›</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-2)' }}>Settings</span>
          </div>
          <button onClick={() => navigate('/dashboard')} style={ghostBtn}>← Dashboard</button>
        </div>
      </header>

      <main style={{ maxWidth: 720, margin: '0 auto', padding: '36px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Settings</h1>

        {/* Account */}
        <Section title="Account">
          <Row label="Email" value={user?.email || '—'} />
          <Row label="Account ID" value={<span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--color-text-2)' }}>{user?.id?.slice(0,16)}…</span>} />
        </Section>

        {/* Appearance */}
        <Section title="Appearance">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>Dark mode</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-2)', marginTop: 2 }}>Reduce eye strain in low-light environments</div>
            </div>
            <Toggle checked={dark} onChange={toggleDark} />
          </div>
        </Section>

        {/* Plan & Billing */}
        <Section title="Plan & Billing">

          {/* Plan badge + usage row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center',
                padding: '4px 12px', borderRadius: 999,
                fontSize: 13, fontWeight: 700,
                background: planColor.bg, color: planColor.text,
                border: `1px solid ${planColor.border}`,
              }}>
                {planMeta.label} Plan
              </span>
              {isPaid && (
                <span style={{ fontSize: 12, color: 'var(--color-text-3)' }}>
                  Active subscription
                </span>
              )}
            </div>

            {isPaid && quota > 0 && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>
                  {used} / {quota} catalogs used
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text-3)' }}>this billing period</div>
              </div>
            )}
          </div>

          {/* Usage bar (paid only) */}
          {isPaid && quota > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{
                height: 6, borderRadius: 999,
                background: 'var(--color-border)',
                overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%', borderRadius: 999,
                  width: `${usePct}%`,
                  background: usePct >= 90 ? 'var(--color-red)' : usePct >= 70 ? 'var(--color-yellow)' : '#4f46e5',
                  transition: 'width 0.4s ease',
                }} />
              </div>
            </div>
          )}

          {/* Paid users: portal button */}
          {isPaid ? (
            <>
              <p style={{ fontSize: 14, color: 'var(--color-text-2)', marginBottom: 16, lineHeight: 1.6 }}>
                Manage your subscription, invoices, and payment method through the billing portal.
              </p>
              {portalErr && (
                <div style={{ background: 'var(--color-red-bg)', border: '1px solid var(--color-red-border)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--color-red-text)', marginBottom: 12 }}>
                  {portalErr}
                </div>
              )}
              <button
                onClick={handleBillingPortal}
                disabled={portalLoading}
                style={{ ...primaryBtn, opacity: portalLoading ? 0.7 : 1, cursor: portalLoading ? 'not-allowed' : 'pointer' }}
              >
                {portalLoading ? 'Opening…' : 'Open Billing Portal'}
              </button>

            </>
          ) : (
            /* Free users: plan cards */
            <>
              <p style={{ fontSize: 14, color: 'var(--color-text-2)', marginBottom: 20, lineHeight: 1.6 }}>
                Upgrade to process more catalogs and unlock full Shopify CSV export.
              </p>
              <PlanCards onUpgrade={handleUpgrade} loadingPlan={upgradingPlan} />
            </>
          )}
        </Section>

        {/* Security */}
        <Section title="Security">
          {resetSent ? (
            <div style={{
              background: 'var(--color-green-bg)', border: '1px solid var(--color-green-border)',
              borderRadius: 8, padding: '10px 14px', fontSize: 14, color: 'var(--color-green-text)',
            }}>
              Password reset email sent to {user?.email}
            </div>
          ) : (
            <>
              {resetErr && (
                <div style={{ background: 'var(--color-red-bg)', border: '1px solid var(--color-red-border)', borderRadius: 8, padding: '10px 14px', fontSize: 14, color: 'var(--color-red-text)', marginBottom: 12 }}>
                  {resetErr}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>Password</div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-2)', marginTop: 2 }}>Send a password reset link to your email</div>
                </div>
                <button onClick={handlePasswordReset} style={ghostBtn}>Send reset link</button>
              </div>
            </>
          )}
        </Section>

        {/* Account actions */}
        <Section title="Account Actions" danger>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>Sign out</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-2)', marginTop: 2 }}>Sign out of your account on this device</div>
            </div>
            <button onClick={handleLogout} style={ghostBtn}>Sign out</button>
          </div>
          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-red-text)' }}>Delete account</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-2)', marginTop: 2 }}>Permanently delete your account and all data</div>
            </div>
            <button onClick={() => setShowDelConf(true)} style={dangerBtn}>Delete account</button>
          </div>
        </Section>
      </main>

      {/* Delete confirmation modal */}
      {showDelConf && (
        <div style={overlayStyle} onClick={() => setShowDelConf(false)}>
          <div style={{ background: 'var(--color-surface)', borderRadius: 14, padding: 28, maxWidth: 400, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>Delete account?</h3>
            <p style={{ fontSize: 14, color: 'var(--color-text-2)', marginBottom: 24, lineHeight: 1.6 }}>
              This will permanently delete your account, all catalogs, and all product data. This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowDelConf(false)} style={ghostBtn}>Cancel</button>
              <button onClick={handleDeleteAccount} disabled={deleting} style={dangerBtn}>
                {deleting ? 'Deleting…' : 'Yes, delete account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children, danger }) {
  return (
    <div style={{
      background: 'var(--color-surface)', borderRadius: 12,
      border: `1px solid ${danger ? 'var(--color-red-border)' : 'var(--color-border)'}`,
      padding: 24,
    }}>
      <h2 style={{ fontSize: 12, fontWeight: 700, marginBottom: 16, color: danger ? 'var(--color-red-text)' : 'var(--color-text)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {title}
      </h2>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid var(--color-border)' }}>
      <span style={{ fontSize: 13, color: 'var(--color-text-2)' }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 500 }}>{value}</span>
    </div>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <button
      onClick={onChange}
      style={{
        width: 44, height: 24, borderRadius: 999, border: 'none',
        background: checked ? 'var(--color-primary)' : 'var(--color-border)',
        cursor: 'pointer', padding: 2, transition: 'background 0.2s',
        position: 'relative', flexShrink: 0,
      }}
    >
      <div style={{
        width: 20, height: 20, borderRadius: '50%', background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        transform: checked ? 'translateX(20px)' : 'translateX(0)',
        transition: 'transform 0.2s',
      }} />
    </button>
  );
}

const primaryBtn = {
  background: 'var(--color-primary)', color: '#fff',
  border: 'none', borderRadius: 8, padding: '9px 18px',
  fontSize: 14, fontWeight: 600, cursor: 'pointer',
};
const ghostBtn = {
  background: 'none', border: '1px solid var(--color-border)',
  borderRadius: 8, padding: '7px 14px', fontSize: 13,
  color: 'var(--color-text-2)', cursor: 'pointer',
};
const dangerBtn = {
  background: 'var(--color-red-bg)', color: 'var(--color-red-text)',
  border: '1px solid var(--color-red-border)', borderRadius: 8,
  padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};
const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
  backdropFilter: 'blur(3px)', zIndex: 50,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
};
